import { RfpTypes } from './rfp-types.enum';
import { RfpOldCategory } from './category';
import { Buyer } from './buyer';
import { RfpStatus } from './rfp-status';
import { RfpActualSpend } from './actual-spend';
import { Statistics } from './statistics';
import { SubmittedBy } from './submitted-by';
import { Participant } from './participant';
import moment from 'moment';
import { Identifier } from '../identifiers/identifiers';
import { Offer } from './offer';

interface DateRange {
  startDate: string;
  endDate: string;
}

export interface RfpRecipient {
  seller: Identifier;
  status?: string;
  offers?: RecipientOffers[];
  offerSubmissionStatistics?: OfferSubmissionStatistics;
}
export interface RecipientOffers {
  id: number;
  name: string;
  status: string;
  submissionDate: Date;
  sellerId: number;
}
export interface OfferSubmissionStatistics {
  newSubmissionsCount?: number;
  lastSubmissionDate?: Date;
}

interface AuditInfo {
  createdDate?: Date;
}

export interface Seller {
  id: number;
  name: string;
  organizationType: string;
}

export class Rfp {
  id!: number;
  category!: RfpOldCategory;
  primaryCategories?: Identifier[];
  leafNodeCategories?: Identifier[];
  status!: RfpStatus;
  offersReceivedDate!: Date;
  contractDurations?: number[];
  contractEffectiveDates?: Date[];
  preferredContractEffectiveDate!: Date;
  preferredContractEffectiveDateRaw?: string;
  preferredContractDuration!: number;
  type!: RfpTypes;
  buyer!: Buyer;
  purchasePlanAccessible!: boolean;
  releaseHoldDate!: Date;
  actualSpend!: RfpActualSpend;
  hasFormularyPrice!: boolean;
  createdBy?: string;

  // Optional Fields
  submissionDate?: Date;
  submittedBy?: SubmittedBy;
  pricingExpirationDate?: Date;
  offersDueDate?: Date;
  hasPartialCategories?: boolean;
  statistics?: Statistics;
  potentialSavings?: {
    savingsVolume: number;
  };
  spendEndDate?: Date;
  spendStartDate?: Date;
  spendDateRange?: {
    startDate: Date;
    endDate: Date;
  };
  spend?: {
    actualSpend: Spend;
    continuedSpend: Spend;
    annualizedSpend: Spend;
  };
  purchasePlanningLevel?: string;
  responseStatus?: string;
  offers!: Array<Offer>;
  participants?: Array<Participant>;

  customSpendDateRange?: {
    bounds: DateRange;
    selected: DateRange;
    hasCustomSpendDateRange: boolean;
  };
  groupRfp?: boolean;
  recipients?: Array<RfpRecipient>;
  isOneLevelCategoryScheme?: boolean;
  publishedDate?: Date;
  isPublishable?: boolean;
  auditInfo?: AuditInfo;

  static organizationTypeIsGroup(organizationType: string) {
    return new RegExp(/group/i).test(organizationType);
  }

  constructor(data: unknown) {
    Object.assign(this, data);

    // Override these with the Date versions for easier coding elsewhere
    this.submissionDate = this.submissionDate
      ? moment(this.submissionDate).toDate()
      : undefined;
    this.offersReceivedDate = moment(this.offersReceivedDate).toDate();
    this.pricingExpirationDate = this.pricingExpirationDate
      ? moment(this.pricingExpirationDate).toDate()
      : undefined;
    this.offersDueDate = this.offersDueDate
      ? moment(this.offersDueDate).toDate()
      : undefined;

    this.preferredContractEffectiveDateRaw = String(
      this.preferredContractEffectiveDate,
    );

    // TODO: Check with kemi and kavya if this value will always have a value
    // if it might be null, we need to check that since new Date creats a date even with null value
    this.preferredContractEffectiveDate = new Date(
      this.preferredContractEffectiveDate,
    );

    if (this.auditInfo) {
      this.auditInfo = {
        createdDate: this.auditInfo.createdDate
          ? moment(this.auditInfo.createdDate).toDate()
          : undefined,
      };
    }
  }
}

interface Spend {
  productCount: number;
  spendVolume: number;
  spendVolumeBracket?: number;
  unitVolume: number;
}
